import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators';
import { getNewUiUrl } from '../helpers/auth-cookie';

/**
 * Banner shown above the legacy editor when the map has been saved
 * through the new editor (`Topic.last_edited_in_new_ui_at` is set).
 * Gentle nudge — the legacy editor still works, but edits made there
 * are bridged to the linked Map via `mirror_legacy_topic_to_map`.
 *
 * The new editor lives at `app.proxi.co/map-editor/<id>` (or the
 * localhost/beta equivalent — see `getNewUiUrl`). The link includes
 * the topic id so the curator lands directly on the same map.
 */
@customElement('new-ui-banner')
export class NewUiBanner extends LitElement {
    /** ISO timestamp from the projected `Topic.last_edited_in_new_ui_at`.
     *  When unset / empty the banner renders nothing. */
    @property({ type: String })
    lastEditedAt: string | null = null;

    /** Topic id (Map id, since they're 1:1 for new-editor maps). Used to
     *  build the deep link into the new editor. Required for the link
     *  to render — without it we surface a generic CTA instead. */
    @property({ type: String })
    topicId: string | null = null;

    /** Builds a human-readable relative time like "2 days ago". Falls
     *  back to a date string for older edits since "365 days ago"
     *  reads worse than "Mar 14, 2025". */
    private formatRelative(iso: string): string {
        const then = Date.parse(iso);
        if (!Number.isFinite(then)) return '';
        const deltaMs = Date.now() - then;
        const deltaMin = Math.floor(deltaMs / 60_000);
        if (deltaMin < 1) return 'just now';
        if (deltaMin < 60) return `${deltaMin} minute${deltaMin === 1 ? '' : 's'} ago`;
        const deltaHr = Math.floor(deltaMin / 60);
        if (deltaHr < 24) return `${deltaHr} hour${deltaHr === 1 ? '' : 's'} ago`;
        const deltaDay = Math.floor(deltaHr / 24);
        if (deltaDay < 30) return `${deltaDay} day${deltaDay === 1 ? '' : 's'} ago`;
        // Older than a month — show the absolute date so it doesn't read
        // as a vague "60 days ago".
        return new Date(then).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }

    render() {
        if (!this.lastEditedAt) return html``;
        const when = this.formatRelative(this.lastEditedAt);
        // Build the deep link only when we have the id — otherwise the
        // banner still shows but with a generic "open new editor" link.
        const href = this.topicId
            ? `${getNewUiUrl()}/map-editor/${this.topicId}`
            : `${getNewUiUrl()}/maps`;
        return html`
            <div class="banner" role="status">
                <div class="content">
                    <span class="icon" aria-hidden="true">✨</span>
                    <span class="message">
                        This map was last edited in the new editor
                        ${when ? html`<strong>${when}</strong>` : ''}.
                        Continue editing there for the latest features.
                    </span>
                </div>
                <a class="cta" href="${href}" target="_top" rel="noopener">
                    Open in new editor →
                </a>
            </div>
        `;
    }

    static styles = css`
        :host {
            display: block;
        }
        .banner {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 8px 16px;
            background: #f0f4ff;
            border-bottom: 1px solid #d6deff;
            color: #1a1f36;
            font-size: 13px;
            line-height: 1.4;
            flex-wrap: wrap;
        }
        .content {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
            min-width: 0;
        }
        .icon {
            flex-shrink: 0;
        }
        .message strong {
            font-weight: 600;
        }
        .cta {
            color: #2c4cdc;
            font-weight: 600;
            text-decoration: none;
            flex-shrink: 0;
            white-space: nowrap;
            padding: 4px 8px;
            border-radius: 4px;
            transition: background 120ms;
        }
        .cta:hover {
            background: rgba(44, 76, 220, 0.08);
            text-decoration: underline;
        }
        @media (max-width: 600px) {
            .banner {
                font-size: 12px;
                padding: 6px 12px;
            }
        }
    `;
}
